import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useLocation } from 'react-router-dom';
import { FormattedMessage, FormattedDuration, FormattedReactMessage } from 'LearningCenterUI/components/I18n';
import { useSaveFavoriteLesson } from '../hooks/useSaveFavoriteContent';
import InProgressDot from './InProgressDot';
import PositionedInlineList from './PositionedInlineList';
import CompletedIcon from './CompletedIcon';
import ReducedCardLayout from './ReducedCardLayout';
import { FILTER_CONSTS } from '../../../../constants/TopFilterConstants';
import UsageTracker from '../../../../utils/UsageTracker';
import { useCallback, useMemo } from 'react'; // @ts-expect-error untyped import

import useFilterParams from '../../../../hooks/useFilterParams';
import { getOverviewModalUrl, OVERVIEW_MODAL_PLAYLIST_PARAM } from '../../../../utils/RoutingUtils';
import { useAssetStatus } from '../../../../features/userProgress';
import { useLesson, useLessonProgressTime } from '../../../../features/lessons';
import { useIsOverview } from '../../../../features/tracks';
import ReducedCardSkeleton from './ReducedCardSkeleton';
import { CardMenuIcon, ReducedCardMenuIconBox } from '../../../../features/cardMenu';

const ReducedLessonCard = ({
  lessonId,
  expandableContent,
  onClick = () => {},
  to,
  use,
  appearsIn,
  position,
  verticalGap,
  showFavoriteTooltip = true,
  isSelected,
  language,
  size,
  customAction,
  playlistId
}) => {
  const {
    lesson,
    lessonLoading
  } = useLesson(lessonId, language);
  const location = useLocation();
  const isOverview = useIsOverview();
  const {
    status,
    allProgressLoading
  } = useAssetStatus({
    id: lessonId,
    type: 'LESSON'
  });
  const {
    remainingTime
  } = useLessonProgressTime(lessonId);
  const totalTime = lesson ? lesson.duration * 1000 : 0;
  const progressAmount = status === FILTER_CONSTS.STATUSES.COMPLETED ? 1 : status === FILTER_CONSTS.STATUSES.IN_PROGRESS ? (totalTime - remainingTime * 1000) / totalTime : 0;
  const toUrl = useMemo(() => {
    if (to) return to;
    const searchParams = new URLSearchParams(location.search);
    if (playlistId) searchParams.set(OVERVIEW_MODAL_PLAYLIST_PARAM, playlistId.toString());

    if (status !== FILTER_CONSTS.STATUSES.NOT_STARTED && !isOverview && !playlistId) {
      return {
        pathname: `/lessons/${lessonId}`,
        search: `${language ? `?language=${language}` : null}`
      };
    }

    return Object.assign({}, location, {
      pathname: getOverviewModalUrl(location.pathname, false, lessonId, appearsIn, language),
      search: searchParams.toString()
    });
  }, [to, status, isOverview, location, lessonId, appearsIn, playlistId, language]);
  const [isSaved, onSaveClick] = useSaveFavoriteLesson({
    lessonId,
    showTooltip: showFavoriteTooltip
  });
  const filterParams = useFilterParams();
  const handleClick = useCallback(() => {
    onClick();
    UsageTracker.track('interaction', {
      action: 'content-card-click',
      contentType: 'LESSON',
      lessonId: lesson && lesson.id,
      lessonType: lesson && lesson.lessonType[0],
      appearsIn,
      position,
      name: lesson && lesson.name,
      filters: JSON.stringify(filterParams),
      style: 'reduced',
      isInProgress: status === FILTER_CONSTS.STATUSES.IN_PROGRESS,
      playlistId
    });
  }, [onClick, lesson, appearsIn, position, filterParams, status, playlistId]);

  if (lessonLoading || allProgressLoading) {
    return /*#__PURE__*/_jsx(ReducedCardSkeleton, {
      size: size,
      use: use
    });
  }

  if (!lessonLoading && !lesson) {
    return null;
  }

  return /*#__PURE__*/_jsx(ReducedCardLayout, {
    contentId: lesson.id,
    customAction: customAction,
    description: lesson.description,
    expandableContent: expandableContent,
    isCompleted: status === FILTER_CONSTS.STATUSES.COMPLETED,
    isSaved: isSaved,
    isSelected: isSelected,
    name: lesson.name,
    onClick: handleClick,
    onSaveClick: onSaveClick,
    progressAmount: progressAmount,
    rightAction: /*#__PURE__*/_jsx(ReducedCardMenuIconBox, {
      className: "m-right-3",
      children: /*#__PURE__*/_jsx(CardMenuIcon, {
        language: language,
        lessonId: lessonId,
        use: use
      })
    }),
    size: size,
    thumbnailUrl: lesson.previewThumbnailSquareUrl,
    to: toUrl,
    use: use,
    verticalGap: verticalGap,
    children: status === FILTER_CONSTS.STATUSES.COMPLETED ? /*#__PURE__*/_jsx(PositionedInlineList, {
      children: /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(CompletedIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.reducedContentCards.completed"
        })]
      })
    }) : status === FILTER_CONSTS.STATUSES.IN_PROGRESS ? /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(InProgressDot, {}), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "learningCenter.reducedContentCards.timeRemaining",
        options: {
          time: /*#__PURE__*/_jsx(FormattedDuration, {
            format: "medium",
            from: +new Date() - remainingTime * 1000
          })
        }
      })]
    }) : /*#__PURE__*/_jsxs(PositionedInlineList, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.resources.type.${lesson.lessonType && lesson.lessonType.length ? lesson.lessonType[0] : FILTER_CONSTS.LESSON_TYPES.LESSON}`
      }), /*#__PURE__*/_jsx(FormattedDuration, {
        format: "medium",
        from: +new Date() - totalTime
      })]
    })
  });
};

export default ReducedLessonCard;